function setOrbeLocalizationPreference(name, value) {
  if (!value) {
    return;
  }

  const fallbackDomain = window.location.hostname.replace(/^www\./, '');
  const shopDomain =
    typeof window.orbito?.getCountryDomain === 'function'
      ? window.orbito.getCountryDomain() || fallbackDomain
      : fallbackDomain;
  const domainAttribute = shopDomain ? `; domain=${shopDomain.replace(/^\./, '')}` : '';

  document.cookie = `${name}=${encodeURIComponent(value)}${domainAttribute}; path=/; max-age=${
    60 * 60 * 24 * 60
  }; SameSite=Lax`;
}

function addOrbeCountryToReturnUrl(form, countryCode) {
  const returnToInput = form?.querySelector('input[name="return_to"]');

  if (!returnToInput || !countryCode) {
    return;
  }

  const returnUrl = new URL(returnToInput.value || window.location.href, window.location.origin);
  returnUrl.searchParams.set('mdApp_countryCodeDomain', countryCode);
  returnToInput.value = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
}

if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button'),
          panel: this.querySelector('.disclosure__list-wrapper'),
        };
        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

        this.querySelectorAll('a').forEach((item) =>
          item.addEventListener('click', this.onItemClick.bind(this)),
        );
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
        this.hidePanel();
        event.stopPropagation();
        this.elements.button.focus();
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        const selectedValue = event.currentTarget.dataset.value;

        this.elements.input.value = selectedValue;

        if (this.elements.input.name === 'country_code') {
          setOrbeLocalizationPreference('mdApp_countryCodeDomain', selectedValue);
          addOrbeCountryToReturnUrl(form, selectedValue);
        } else {
          setOrbeLocalizationPreference('mdApp_showRecommendationLang', selectedValue);
        }

        if (form) form.submit();
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString(),
        );
      }

      closeSelector(event) {
        const isChild =
          this.elements.panel.contains(event.relatedTarget) ||
          this.elements.button.contains(event.relatedTarget);
        if (!event.relatedTarget || !isChild) {
          this.hidePanel();
        }
      }
    },
  );
}

if (!customElements.get('localization-form-modal')) {
  customElements.define(
    'localization-form-modal',
    class LocalizationFormModal extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.storageKey = 'shopify_country_code';

        this.elements = {
          localeInput: this.querySelector('input[name="locale_code"]'),
          countryInput: this.querySelector('input[name="country_code"]'),
          langContainer: this.querySelector('input[name="locale_code"]')?.closest(
            'disclosure-list',
          ),
          countryContainer: this.querySelector('input[name="country_code"]')?.closest(
            'disclosure-list',
          ),
        };
      }

      connectedCallback() {
        this.initLanguageState();
        this.initCountryState();

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }
      }

      initLanguageState() {
        if (!this.elements.langContainer) {
          return;
        }

        const isFrench =
          window.location.pathname.startsWith('/fr/') || window.location.pathname === '/fr';
        const targetValue = isFrench ? 'fr' : 'en';

        if (this.elements.localeInput) {
          this.elements.localeInput.value = targetValue;
        }

        this._updateDisclosureUI(this.elements.langContainer, targetValue);
      }

      initCountryState() {
        if (!this.elements.countryContainer) {
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const urlCountry = urlParams.get('country_code');
        const storedCountry = localStorage.getItem(this.storageKey);

        let finalCountry = null;

        if (urlCountry) {
          finalCountry = urlCountry;
          localStorage.setItem(this.storageKey, urlCountry);
        } else if (storedCountry) {
          finalCountry = storedCountry;
        }

        if (finalCountry) {
          if (this.elements.countryInput) {
            this.elements.countryInput.value = finalCountry;
          }

          this._updateDisclosureUI(this.elements.countryContainer, finalCountry);
        }
      }

      _updateDisclosureUI(container, value) {
        const items = container.querySelectorAll('.disclosure__item');
        const button = container.querySelector('.disclosure__button');
        let selectedText = '';

        items.forEach((item) => {
          const link = item.querySelector('a, span');
          const itemValue = item.dataset.value || link?.dataset.value;

          if (itemValue === value) {
            item.setAttribute('aria-current', 'true');
            link?.classList.add('disclosure__link--active');

            selectedText = item.dataset.content || link?.innerText || link?.textContent;
          } else {
            item.removeAttribute('aria-current');
            link?.classList.remove('disclosure__link--active');
          }
        });

        if (button && selectedText) {
          const caret = button.querySelector('svg');

          button.textContent = selectedText.trim() + ' ';

          if (caret) {
            button.appendChild(caret);
          }
        }

        container.dataset.select = value;
        container.dataset.value = selectedText?.trim();
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        const formData = new FormData(this.form);
        const localeCode = formData.get('locale_code');
        const countryCode = formData.get('country_code');

        if (countryCode) {
          localStorage.setItem(this.storageKey, countryCode);
          setOrbeLocalizationPreference('mdApp_countryCodeDomain', countryCode);
        }

        if (localeCode) {
          setOrbeLocalizationPreference('mdApp_showRecommendationLang', localeCode);
        }

        try {
          await fetch(`${window.Shopify.routes.root}localization`, {
            method: 'POST',
            body: formData,
          });
        } catch (e) {
          console.error('Failed to update Shopify localization session:', e);
        }

        let currentPath = window.location.pathname;
        let newPath = currentPath;

        if (newPath.startsWith('/fr/')) {
          newPath = '/' + newPath.substring(4);
        } else if (newPath === '/fr') {
          newPath = '/';
        }

        if (localeCode === 'fr') {
          newPath = '/fr' + (newPath === '/' ? '' : newPath);
        }

        const url = new URL(window.location.origin + newPath + window.location.search);

        if (countryCode) {
          url.searchParams.set('country_code', countryCode);
          url.searchParams.set('mdApp_countryCodeDomain', countryCode);
        }

        window.location.href = url.toString();
      }

      hidePanel() {
        this.querySelectorAll('[aria-expanded]').forEach((btn) =>
          btn.setAttribute('aria-expanded', 'false'),
        );
        this.querySelectorAll('ul.disclosure__list').forEach((list) =>
          list.setAttribute('hidden', 'true'),
        );
      }
    },
  );
}
